/**
 * Product analytics — identify the signed-in user in PostHog.
 *
 * Once per session, when a Clerk session becomes signed-in, identify the
 * user in PostHog (by user id only — see `lib/analytics.ts`), tagging the
 * person with whatever first-touch UTM attribution is stored locally.
 *
 * `signup_completed` itself is NOT fired here. It's captured server-side
 * (see `requireAuth.ts` in the API server) at the moment the user's DB row
 * is first provisioned — a durable, exactly-once signal, unlike a
 * client-side "Clerk account created in the last N minutes" heuristic,
 * which can miss slow signups (email verification, OAuth redirects).
 */

import { useEffect, useRef } from 'react';
import { useUser } from '@clerk/react';
import { identifyUser } from '@/lib/analytics';

export function useAnalyticsIdentify(): void {
  const { isSignedIn, user } = useUser();
  const handled = useRef(false);

  useEffect(() => {
    if (!isSignedIn || !user || handled.current) return;
    handled.current = true;

    identifyUser(user.id);
  }, [isSignedIn, user]);
}
